import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export const useStoryStore = create((set, get) => ({
  storyGroups: [],
  isLoading: false,
  isPosting: false,

  // Modal states
  isCreateOpen: false,
  isViewerOpen: false,
  activeUserIndex: 0,
  activeStoryIndex: 0,

  openCreate: () => set({ isCreateOpen: true }),
  closeCreate: () => set({ isCreateOpen: false }),

  openViewer: (userIndex, storyIndex = 0) =>
    set({
      isViewerOpen: true,
      activeUserIndex: Math.max(0, userIndex),
      activeStoryIndex: Math.max(0, storyIndex),
    }),

  closeViewer: () =>
    set({
      isViewerOpen: false,
      activeStoryIndex: 0,
    }),

  setActiveStoryIndex: (index) => set({ activeStoryIndex: index }),
  setActiveUserIndex: (index) => set({ activeUserIndex: index, activeStoryIndex: 0 }),

  // ── Fetch all active stories ──────────────────────────────────────────────
  fetchStories: async () => {
    set({ isLoading: true });
    try {
      const res = await axiosInstance.get("/stories");
      set({ storyGroups: res.data || [] });
    } catch (err) {
      console.error("[StoryStore] Failed to fetch stories:", err);
    } finally {
      set({ isLoading: false });
    }
  },

  // ── Create a new 24h story / vibe ─────────────────────────────────────────
  createStory: async (storyData) => {
    set({ isPosting: true });
    try {
      const res = await axiosInstance.post("/stories", storyData);
      toast.success("✨ Your 24h Vibe Story is live!");
      set({ isCreateOpen: false });
      await get().fetchStories();
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to post story.";
      toast.error(msg);
      throw err;
    } finally {
      set({ isPosting: false });
    }
  },

  // ── Mark story as viewed ──────────────────────────────────────────────────
  viewStory: async (storyId) => {
    try {
      // 1. Update local state immediately so rings change color in real-time
      const { storyGroups, activeUserIndex } = get();
      if (storyGroups[activeUserIndex]) {
        const updated = [...storyGroups];
        const group = { ...updated[activeUserIndex] };
        group.stories = group.stories.map((s) => {
          if (s._id === storyId) {
            return { ...s, hasSeen: true };
          }
          return s;
        });

        // If all stories in this group are seen, mark group as seen
        const hasAnyUnseen = group.stories.some((s) => !s.hasSeen);
        group.hasUnseen = hasAnyUnseen;

        updated[activeUserIndex] = group;
        set({ storyGroups: updated });
      }

      // 2. Persist view to server
      await axiosInstance.post(`/stories/${storyId}/view`);
    } catch (err) {
      // Non-blocking
    }
  },

  // ── React to a story ──────────────────────────────────────────────────────
  reactToStory: async (storyId, emoji = "🔥") => {
    try {
      await axiosInstance.post(`/stories/${storyId}/react`, { emoji });
      toast.success(`Sent ${emoji} reaction!`);
    } catch (err) {
      console.error("Failed to react to story:", err);
    }
  },

  // ── Delete own story ──────────────────────────────────────────────────────
  deleteStory: async (storyId) => {
    try {
      await axiosInstance.delete(`/stories/${storyId}`);
      toast.success("Story removed.");
      await get().fetchStories();
      get().closeViewer();
    } catch (err) {
      toast.error("Failed to delete story.");
    }
  },
}));
